// src/Client/apps/user/src/hooks/useGetMapBoundary.ts
import { ServiceConfig } from '@user/store/services/config'
import { get, isUnexpectedResponseError } from '@user/store/services/crud'
import { HereResult } from '@user/store/services/hereGeoCoder'
import { useCallback, useRef, useState } from 'react'
import { inferBoundaryQuery } from './boundaryUtils'

const apiBase = `${ServiceConfig.baseAPIUrl}/mapgeodata`

type LatLng = { lat: number; lng: number }
type BoundaryResponse = {
  rings: LatLng[][]
  featureName?: string | null
  featureId?: string | null
}

export interface DrawOptions {
  lineWidth?: number
  lineColor?: string
  fillColor?: string
  groupZIndex?: number
  replaceExisting?: boolean
  fitView?: boolean
  lineDash?: number[]
  lineCap?: 'butt' | 'round' | 'square'
  country?: string
}

/**
 * useGetMapBoundary
 * - infers regionType/regionId/state from a HERE result
 * - calls /api/map-geo-data/boundary
 * - draws the boundary on a HERE map with proper hole handling
 * - Rings inside other rings become holes (subtraction), not separate regions
 */
export function useGetMapBoundary() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [meta, setMeta] = useState<{
    featureName?: string | null
    featureId?: string | null
  } | null>(null)

  const drawnGroupRef = useRef<H.map.Group | null>(null)

  const clearBoundary = useCallback((map: H.Map) => {
    try {
      // Remove the last drawn group if we tracked it
      if (drawnGroupRef.current) {
        map.removeObject(drawnGroupRef.current)
        drawnGroupRef.current = null
      }

      // Defensive: also remove any groups previously tagged as boundaries
      const objs = (map.getObjects && map.getObjects()) || []
      const toRemove: H.map.Object[] = []
      for (const obj of objs) {
        const anyObj = obj as any
        const data =
          typeof anyObj.getData === 'function' ? anyObj.getData() : undefined
        if (data && data.__boundary === true) {
          toRemove.push(obj)
        }
      }
      if (toRemove.length) map.removeObjects(toRemove)
    } catch {
      // best-effort cleanup
    }
  }, [])

  const ringsToHerePolygons = useCallback((rings: LatLng[][]) => {
    if (!rings.length) return []

    const toLine = (ring: LatLng[]) => {
      const ls = new H.geo.LineString()
      for (const p of ring) ls.pushPoint({ lat: p.lat, lng: p.lng })
      return ls
    }

    // Calculate signed area to determine winding order
    const getSignedArea = (ring: LatLng[]): number => {
      let area = 0
      for (let i = 0; i < ring.length - 1; i++) {
        const j = i + 1
        area += (ring[j].lng - ring[i].lng) * (ring[j].lat + ring[i].lat)
      }
      return area / 2
    }

    // Sort rings by absolute area (largest first) and separate by winding
    const ringsWithArea = rings
      .map((ring) => ({
        ring,
        signedArea: getSignedArea(ring),
        absArea: Math.abs(getSignedArea(ring)),
      }))
      .filter((r) => r.ring.length >= 3) // Filter out invalid rings
      .sort((a, b) => b.absArea - a.absArea) // Largest area first

    if (!ringsWithArea.length) return []

    // Create polygons with holes
    // The largest ring(s) with positive area are exterior boundaries
    // Smaller rings with negative area (or opposite winding) are holes
    const polygons: H.map.Polygon[] = []
    const usedRings = new Set<number>()

    for (let i = 0; i < ringsWithArea.length; i++) {
      if (usedRings.has(i)) continue

      const mainRing = ringsWithArea[i]
      const exteriorLine = toLine(mainRing.ring)

      // Find potential holes (rings contained within this exterior ring)
      const holes: H.geo.LineString[] = []

      for (let j = i + 1; j < ringsWithArea.length; j++) {
        if (usedRings.has(j)) continue

        const candidateHole = ringsWithArea[j]

        // Check if this ring has opposite winding or is significantly smaller
        const isOppositeWinding =
          mainRing.signedArea > 0 !== candidateHole.signedArea > 0
        const isMuchSmaller = candidateHole.absArea < mainRing.absArea * 0.8

        if (isOppositeWinding || isMuchSmaller) {
          holes.push(toLine(candidateHole.ring))
          usedRings.add(j)
        }
      }

      // Create polygon with exterior boundary and holes
      const geoPoly =
        holes.length > 0
          ? new H.geo.Polygon(exteriorLine, holes)
          : new H.geo.Polygon(exteriorLine)

      const mapPoly = new H.map.Polygon(geoPoly)
      polygons.push(mapPoly)
      usedRings.add(i)
    }

    return polygons
  }, [])

  /**
   * Draw a boundary for a HERE result.
   * Returns the created H.map.Group (polygon container) or null.
   */
  const drawBoundaryForHereResult = useCallback(
    async (map: H.Map, result: HereResult, options: DrawOptions = {}) => {
      setError(null)
      setMeta(null)

      const {
        lineWidth = 2,
        lineColor = '#ff0000',
        fillColor = 'rgba(255, 0, 0, 0.05)',
        lineDash = [2, 4],
        lineCap = 'round',
        groupZIndex = 5,
        fitView = true,
      } = options

      clearBoundary(map)

      const query = inferBoundaryQuery(result)
      if (!query) {
        setError('Could not infer a mappable region from the HERE result.')
        return null
      }

      setLoading(true)
      try {
        const params = new URLSearchParams({
          regionType: query.regionType,
          regionId: query.regionId,
        })
        if (query.state) params.set('state', query.state)
        // Use country from options if provided, otherwise use inferred country from query
        const country = options.country ?? query.country
        if (country) params.set('country', country)

        const resp = await get(`${apiBase}/boundary?${params.toString()}`)

        if (isUnexpectedResponseError(resp)) {
          let msg = ''
          try {
            // The json body can be a string or an object with a message
            const errBody = await resp.json()
            if (typeof errBody === 'string') {
              const parsed = JSON.parse(errBody)
              msg = parsed.message
            } else if (
              errBody &&
              typeof errBody === 'object' &&
              'message' in errBody
            ) {
              msg = (errBody as { message: string }).message
            }
          } catch {
            // ignore if json parsing fails
          }
          throw new Error(`${resp.status}${msg ? ` — ${msg}` : ''}`)
        }
        const data: BoundaryResponse = await (resp as Response).json()

        const polygons = ringsToHerePolygons(data.rings)
        if (!polygons.length) {
          setError('Boundary has no rings.')
          return null
        }

        // Apply style to all polygons
        polygons.forEach((poly) => {
          poly.setStyle({
            strokeColor: lineColor,
            lineWidth,
            fillColor,
            lineDash,
            lineCap,
          })
        })

        const group = new H.map.Group({ zIndex: groupZIndex })
        polygons.forEach((poly) => group.addObject(poly))
        // tag so we can find it even if our ref is lost
        ;(group as any).setData?.({ __boundary: true })
        map.addObject(group)
        drawnGroupRef.current = group

        if (fitView) {
          // Calculate bounding box that encompasses all polygons
          let combinedBounds: H.geo.Rect | null = null

          for (const poly of polygons) {
            const bounds = poly.getBoundingBox()
            if (bounds) {
              if (!combinedBounds) {
                combinedBounds = bounds
              } else {
                // Expand the combined bounds to include this polygon's bounds
                const north = Math.max(combinedBounds.getTop(), bounds.getTop())
                const south = Math.min(
                  combinedBounds.getBottom(),
                  bounds.getBottom(),
                )
                const east = Math.max(
                  combinedBounds.getRight(),
                  bounds.getRight(),
                )
                const west = Math.min(
                  combinedBounds.getLeft(),
                  bounds.getLeft(),
                )
                combinedBounds = new H.geo.Rect(north, west, south, east)
              }
            }
          }

          if (combinedBounds) {
            map.getViewModel().setLookAtData({ bounds: combinedBounds })
          }
        }

        setMeta({
          featureName: data.featureName ?? null,
          featureId: data.featureId ?? null,
        })
        return group
      } catch (e: any) {
        setError(e?.message ?? 'Failed to fetch/draw boundary.')
        return null
      } finally {
        setLoading(false)
      }
    },
    [clearBoundary, ringsToHerePolygons],
  )

  /**
   * Optional direct helper when you already know (state, regionType, regionId)
   * and don’t have a HERE result.
   */
  const drawBoundary = useCallback(
    async (
      map: H.Map,
      regionType: 'zip' | 'county' | 'city' | 'state',
      regionId: string,
      state?: string,
      country?: string,
      options: DrawOptions = {},
    ) => {
      // Merge country into options if provided as parameter
      const mergedOptions = country ? { ...options, country } : options

      return drawBoundaryForHereResult(
        map,
        {
          title: '',
          id: '',
          resultType:
            regionType === 'state'
              ? 'administrativeArea'
              : regionType === 'zip'
                ? 'locality'
                : 'locality',
          address: {
            label: '',
            countryCode: country === 'ca' ? 'CAN' : 'USA',
            countryName: country === 'ca' ? 'Canada' : 'United States',
            stateCode: state ?? '',
            state: '',
            county: regionType === 'county' ? regionId : '',
            city: regionType === 'city' ? regionId : '',
            district: '',
            postalCode: regionType === 'zip' ? regionId : '',
            houseNumber: '',
          },
          position: { lat: 0, lng: 0 },
          mapView: { north: 0, south: 0, east: 0, west: 0 },
        } as HereResult,
        mergedOptions,
      )
    },
    [drawBoundaryForHereResult],
  )

  return {
    drawBoundaryForHereResult,
    drawBoundary,
    clearBoundary: (map: H.Map) => clearBoundary(map),
    loading,
    error,
    meta,
  }
}
